import React, { useState, useEffect, useRef, useMemo } from "react";
import MasterLayout from "../Sections/MasterLayout";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  FormControlLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add,
  Remove,
  InfoOutlined,
  Search as SearchIcon,
} from "@mui/icons-material";
import { ChevronDown, Plus, X, Pencil } from "lucide-react";
import {
  product,
  server_post_json,
  get_user_data,
  create_admin_order
} from "../../ServiceConnection/serviceconnection";
import toast from "react-hot-toast";
import {  useNavigate } from "react-router-dom";
const LANGUAGES = ["English", "Hindi"];
const COUNTRIES = ["India"];
const STATES = ["Delhi", "Maharashtra", "Karnataka", "Tamil Nadu", "Gujarat"];

export default function CreateOrder() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [productModal, setProductModal] = useState(false);
  const [customerModal, setCustomerModal] = useState(false);
  const [discountModal, setDiscountModal] = useState(false);
  const [shippingModal, setShippingModal] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerDropdown, setCustomerDropdown] = useState(false);
  const [prefillCustomerName, setPrefillCustomerName] = useState("");
  const [applyAutoDiscounts, setApplyAutoDiscounts] = useState(true);
  const [addCustomDiscount, setAddCustomDiscount] = useState(false);
  const [discountType, setDiscountType] = useState("Amount");
  const [discountValue, setDiscountValue] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const [shippingValue, setShippingValue] = useState(0);
  const [shippingReason, setShippingReason] = useState("");
  const [customers, setCustomers] = useState([]); 
  const [PRODUCTS, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  // Calculations
  const itemCount = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);
  const IGST_RATE = 18;
  const igst = useMemo(() => subtotal - subtotal / (1 + IGST_RATE / 100), [subtotal]);
  const total = useMemo(() => subtotal - discount + shipping, [subtotal, discount, shipping]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, usersRes] = await Promise.all([
          server_post_json(product),
          server_post_json(get_user_data)
        ]);
        
        // Ensure products and customers are arrays and have valid structure
        setProducts(Array.isArray(productsRes.data?.products) ? productsRes.data.products : []);
        
        const fetchedCustomers = Array.isArray(usersRes.data?.data) ? usersRes.data.data : [];
        // Filter out invalid customers and ensure name exists
        const validCustomers = fetchedCustomers
        .filter(c => c && (c.firstName || c.lastName || c.email || c.phone))
        .map((c, index) => ({
          id: c.user_id || index + 1,
          user_id: c.user_id,
          // basic
          name: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed Customer",
          firstName: c.firstName || "",
          lastName: c.lastName || "",
          email: c.email || "",
          phone: c.phone || "",
      
          // address (🔥 THIS WAS MISSING)
          address: c.address || "",
          city: c.city || "",
          state: c.state || "",
          pincode: c.pincode || "",
          country: c.country || "",
          ordersCount: Number(c.ordersCount || 0),
        }));
      
      setCustomers(validCustomers);
      
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  /* ================= CREATE ORDER ================= */
const handleCreateOrder = async () => {
  if (!customer) {
    toast.error("Please select customer");
    return;
  }

  if (items.length === 0) {
    toast.error("Please add at least one product");
    return;
  }
  if (!customer.address || !customer.city || !customer.state) {
    toast.error("Customer shipping address is incomplete");
    return;
  }
  const payload = {
    user_id: customer.user_id,

    items: items.map((i) => ({
      product_id: i.product_id || "",
      title: i.product,
      size: i.variant,
      quantity: i.qty,
      price: i.price,
      qikink_sku: i.qikink_sku,
      design_url: i.image,
    })),

    shippingAddress: {
      name: customer.name,
      phone: customer.phone,
      address: customer.address || "NA",
      city: customer.city || "NA",
      state: customer.state || "NA",
      pincode: customer.pincode || "000000",
    },

    paymentMethod: "COD",
    paymentStatus: "paid",
    subtotal,
    shippingCharge: shipping,
    totalAmount: total,
  };

  try {
    const res = await server_post_json(create_admin_order, payload);

    toast.success("Order created successfully");

    navigate(`/orders/${res.data.order_id}`);
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to create order");
  }
};
  // Customer dropdown outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCustomerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handlers
  const toggleVariant = (vid) => {
    setSelectedVariants(prev =>
      prev.includes(vid) ? prev.filter(x => x !== vid) : [...prev, vid]
    );
  };

  const addVariants = () => {
    const added = [];
    PRODUCTS.forEach(p => {
      p.variants.forEach(colorGroup => {
        colorGroup.variants.forEach(sizeVar => {
          const vid = `${p._id}-${colorGroup.color}-${sizeVar.size}`;
          if (selectedVariants.includes(vid) && !items.find(i => i.id === vid)) {
            added.push({
              id: vid,
              product: p.title,
              variant: `${colorGroup.color} / ${sizeVar.size}`,
              price: p.price,
              image: p.mainImages?.[0] || '',
              qty: 1,
              qikink_sku: sizeVar.code,
            });
          }
        });
      });
    });
    setItems(prev => [...prev, ...added]);
    setSelectedVariants([]);
    setProductModal(false);
  };

  const updateQty = (id, delta) => 
    setItems(items.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));

  const removeItem = id => setItems(items.filter(i => i.id !== id));

  // Create new customer
  const createCustomer = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const firstName = formData.get("firstName")?.trim() || "";
    const lastName = formData.get("lastName")?.trim() || "";
    const name = `${firstName} ${lastName}`.trim() || "New Customer";

    const newCustomer = {
      user_id: `ADMIN_${Date.now()}`,
      name,
      email: formData.get("email")?.trim() || null,
      phone: formData.get("phone")?.trim() || null,
    };

    // Add to customers list
    setCustomers(prev => [...prev, newCustomer]);
    setCustomer(newCustomer);
    setCustomerModal(false);
    setPrefillCustomerName("");
    toast.success("Customer created successfully");
  };

  const saveDiscount = () => {
    let applied = 0;
    if (addCustomDiscount && discountValue > 0) {
      applied = discountType === "Percentage" 
        ? (subtotal * discountValue) / 100 
        : discountValue;
    }
    setDiscount(applied);
    setDiscountModal(false);
    setAddCustomDiscount(false);
    setDiscountValue(0);
    setDiscountReason("");
  };

  const saveShipping = () => {
    setShipping(shippingValue);
    setShippingModal(false);
    setShippingValue(0);
    setShippingReason("");
  };

  // Filter products by search
  const filteredProducts = PRODUCTS.filter(p =>
    p.title?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Safe customer search filter
  // Replace the entire filteredCustomers useMemo
const filteredCustomers = useMemo(() => {
  if (!Array.isArray(customers)) return [];

  const search = customerSearch.trim().toLowerCase();
  if (search === "") return customers;

  return customers.filter((c) => 
    c && c.name && c.name.toLowerCase().includes(search)
  );
}, [customers, customerSearch]);
const shouldShowCreateOption = customerSearch.trim() !== "" &&
  !customers.some(c => c && c.name && c.name.toLowerCase() === customerSearch.trim().toLowerCase());
  return (
    <MasterLayout>
      <div className="bg-gray-100 min-h-screen p-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Left: Products & Payment */}
          <div className="lg:col-span-2 space-y-3">
            {/* Products List */}
            <div className="bg-white rounded-xl border p-5">
              <div className="flex justify-between mb-4">
                <h2 className="font-semibold">Products</h2>
                <Button size="small" variant="outlined" onClick={() => { setProductModal(true); setProductSearch(""); }}>
                  Browse
                </Button>
              </div>
              {items.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No products added yet</p>
              ) : (
                items.map(i => (
                  <div key={i.id} className="flex items-center justify-between border-t py-4 first:border-t-0">
                    <div className="flex items-center gap-3">
                      <img src={i.image || "https://via.placeholder.com/48"} alt={i.product} className="w-12 h-12 rounded object-cover" />
                      <div>
                        <p className="text-sm font-medium">{i.product}</p>
                        <p className="text-xs text-gray-500">{i.variant}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center border rounded">
                        <IconButton size="small" onClick={() => updateQty(i.id, -1)}><Remove fontSize="small" /></IconButton>
                        <span className="px-3 text-sm">{i.qty}</span>
                        <IconButton size="small" onClick={() => updateQty(i.id, 1)}><Add fontSize="small" /></IconButton>
                      </div>
                      <p className="text-sm font-medium min-w-[80px] text-right">₹{(i.price * i.qty).toFixed(2)}</p>
                      <IconButton onClick={() => removeItem(i.id)}><CloseIcon fontSize="small" /></IconButton>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Payment Summary */}
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-semibold mb-4">Payment</h2>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-3 items-center">
                  <span>Subtotal</span>
                  <span className="text-center text-gray-600">{itemCount} item{itemCount !== 1 && "s"}</span>
                  <span className="text-right">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-3 cursor-pointer text-blue-600 hover:bg-blue-50 -mx-2 px-2 py-1 rounded" onClick={() => setDiscountModal(true)}>
                  <span>Add discount</span>
                  <span className="text-center">—</span>
                  <span className="text-right">-₹{discount.toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-3 cursor-pointer text-blue-600 hover:bg-blue-50 -mx-2 px-2 py-1 rounded" onClick={() => setShippingModal(true)}>
                  <span>Add shipping or delivery</span>
                  <span className="text-center">—</span>
                  <span className="text-right">+₹{shipping.toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-3 text-gray-600">
                  <span className="flex items-center gap-1">Estimated tax <Tooltip title="Included in total"><InfoOutlined fontSize="inherit" /></Tooltip></span>
                  <span className="text-center">IGST 18% (Included)</span>
                  <span className="text-right">₹{igst.toFixed(2)}</span>
                </div>
                <Divider />
                <div className="grid grid-cols-3 font-semibold text-lg">
                  <span>Total</span>
                  <span></span>
                  <span className="text-right">₹{total.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
              <Button
    variant="contained"
    color="primary"
    onClick={handleCreateOrder}
    disabled={loading}
  >
    Create Order
  </Button>
              </div>
            </div>
          </div>

          {/* Right: Customer */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-5">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-semibold">Customer</h2>
                {customer && (
                  <button onClick={() => { setCustomer(null); setCustomerSearch(""); }}>
                    <X size={18} className="text-gray-500 hover:text-black" />
                  </button>
                )}
              </div>

              {customer ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-blue-600 font-medium">{customer.name}</p>
                    <p className="text-gray-500">
  {customer.ordersCount > 0
    ? `${customer.ordersCount} order${customer.ordersCount !== 1 ? "s" : ""}`
    : "No orders"}
</p>

                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <p className="font-medium">Contact information</p>
                      <Pencil size={16} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500">{customer.email || "No email provided"}</p>
                    <p className="text-gray-700">{customer.phone || ""}</p>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <p className="font-medium">Shipping address</p>
                      <Pencil size={16} className="text-gray-400" />
                    </div>
                    {customer.address ? (
  <p className="text-gray-700 text-sm leading-relaxed">
    {customer.address}
    <br />
    {customer.city}, {customer.state} - {customer.pincode}
    <br />
    {customer.country}
  </p>
) : (
  <p className="text-gray-500">No shipping address provided</p>
)}

                  </div>
              
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <div
                    className="flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer hover:border-blue-500 transition-colors"
                    onClick={() => setCustomerDropdown(true)}
                  >
                    <input
                      type="text"
                      placeholder="Search or create customer"
                      className="outline-none flex-1 text-sm bordr_none_clss"
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setCustomerDropdown(true);
                      }}
                      onFocus={() => setCustomerDropdown(true)}
                    />
                    <ChevronDown 
                      size={18} 
                      className={`text-gray-500 transition-transform ${customerDropdown ? 'rotate-180' : ''}`} 
                    />
                  </div>

                  {/* Dropdown */}
                  {customerDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                      {/* Show filtered customers */}
                      {filteredCustomers.map((c) => (
                        <div
                          key={c.id}
                          className="px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => {
                            setCustomer(c);
                            setCustomerSearch("");
                            setCustomerDropdown(false);
                          }}
                        >
                          <div className="font-medium text-sm">{c.name || "Unnamed Customer"}</div>
                          <div className="text-xs text-gray-500">
                            {c.email || c.phone || "No contact info"}
                          </div>
                        </div>
                      ))}

                      {/* Create new customer option */}
                      {shouldShowCreateOption && (
                        <div
                          className="px-4 py-3 border-t hover:bg-gray-100 cursor-pointer flex items-center gap-2 transition-colors"
                          onClick={() => {
                            setPrefillCustomerName(customerSearch.trim());
                            setCustomerDropdown(false);
                            setCustomerModal(true);
                          }}
                        >
                          <Plus size={16} className="text-blue-600" />
                          <span className="text-sm">
                            Create <strong>"{customerSearch.trim()}"</strong> as new customer
                          </span>
                        </div>
                      )}

                      {/* Empty states */}
                      {customers.length === 0 && customerSearch.trim() === "" && (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          No customers yet
                        </div>
                      )}

                      {customerSearch.trim() !== "" && filteredCustomers.length === 0 && !shouldShowCreateOption && (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          No customers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

     
      {/* Product Modal - Shopify Style */}
      <Dialog open={productModal} onClose={() => setProductModal(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 0 }}>
          <div className="flex items-center justify-between">
            <Typography variant="h6" fontWeight="semibold">Select products</Typography>
            <IconButton onClick={() => setProductModal(false)}><CloseIcon /></IconButton>
          </div>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 0 }}>
          {/* Search Bar */}
          <div className="flex gap-3 mb-4 search_br_modl">
            <TextField
              fullWidth
              placeholder="Search products"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </div>

          {/* Table Header - Sticky */}
          <div className="sticky top-0 bg-gray-50 border-b border-t py-2 z-10">
            <div className="grid grid-cols-12 text-sm font-medium text-gray-600 px-4">
              <div className="col-span-7">Product</div>
              <div className="col-span-3 text-center">Available</div>
              <div className="col-span-2 text-right">Price</div>
            </div>
          </div>

          {/* Products List */}
          <div className="max-h-96 overflow-y-auto">
            {filteredProducts.map(p => (
              <div key={p._id} className="border-b">
                {/* Product Title Row */}
                <div className="grid grid-cols-12 items-center py-4 px-4 bg-white hover:bg-gray-50">
                  <div className="col-span-7 flex items-center gap-3">
                    <Checkbox disabled />
                    {p.mainImages?.[0] ? (
                      <img src={p.mainImages[0]} alt={p.title} className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded" />
                    )}
                    <span className="font-medium">"{p.title}"</span>
                  </div>
                  <div className="col-span-3 text-center text-gray-500">-</div>
                  <div className="col-span-2 text-right font-medium">₹{p.price.toFixed(2)} INR</div>
                </div>

                {/* Variants */}
                {p.variants.map(colorGroup => (
                  <div key={colorGroup.color}>
                    {colorGroup.variants.map(sizeVar => {
                      const vid = `${p._id}-${colorGroup.color}-${sizeVar.size}`;
                      const checked = selectedVariants.includes(vid);
                      return (
                        <div
                          key={vid}
                          className={`grid grid-cols-12 items-center py-3 px-4 hover:bg-gray-50 cursor-pointer ${checked ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleVariant(vid)}
                        >
                          <div className="col-span-7 flex items-center gap-3 pl-12">
                            <Checkbox checked={checked} onChange={() => toggleVariant(vid)} onClick={(e) => e.stopPropagation()} />
                            <span className="text-sm">
                              {colorGroup.color} / {sizeVar.size}
                            </span>
                          </div>
                          <div className="col-span-3 text-center">{sizeVar.available}</div>
                          <div className="col-span-2 text-right font-medium">₹{p.price.toFixed(2)} INR</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>

        {/* Fixed Bottom Bar */}
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', py: 2, justifyContent: 'space-between', position: 'sticky', bottom: 0, bgcolor: 'white' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedVariants.length}/500 variants selected
          </Typography>
          <div className="flex gap-2">
            <Button onClick={() => setProductModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={addVariants}
              disabled={selectedVariants.length === 0}
            >
              Add
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Customer Modal */}
      <Dialog open={customerModal} onClose={() => setCustomerModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="border-b">
          Create a new customer
          <IconButton onClick={() => setCustomerModal(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form id="customer-form" onSubmit={createCustomer} className="space-y-4 pt-4">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField name="firstName" label="First name" fullWidth required defaultValue={prefillCustomerName.split(" ")[0] || ""} />
              </Grid>
              <Grid item xs={6}>
                <TextField name="lastName" label="Last name" fullWidth required defaultValue={prefillCustomerName.split(" ").slice(1).join(" ") || ""} />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select defaultValue={LANGUAGES[0]}>
                {LANGUAGES.map(lang => <MenuItem key={lang} value={lang}>{lang}</MenuItem>)}
              </Select>
              <Typography variant="caption" color="textSecondary">
                This customer will receive notifications in this language.
              </Typography>
            </FormControl>
            <TextField name="email" label="Email" type="email" fullWidth />
            <FormControl fullWidth>
              <InputLabel>Country/region</InputLabel>
              <Select defaultValue={COUNTRIES[0]}>
                {COUNTRIES.map(country => <MenuItem key={country} value={country}>{country}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField name="company" label="Company" fullWidth />
            <TextField
              name="address"
              label="Address"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TextField name="apartment" label="Apartment, suite, etc" fullWidth />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField name="city" label="City" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select defaultValue={STATES[0]}>
                    {STATES.map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField name="pinCode" label="PIN code" fullWidth />
            <TextField
              name="phone"
              label="Phone"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><img src="https://flagcdn.com/in.svg" alt="India" width="20" /></InputAdornment>,
              }}
            />
          </form>
        </DialogContent>
        <DialogActions className="border-t">
          <Button onClick={() => setCustomerModal(false)}>Cancel</Button>
          <Button type="submit" variant="contained" form="customer-form">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={discountModal} onClose={() => setDiscountModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="border-b">
          Add discount
          <IconButton onClick={() => setDiscountModal(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="space-y-4 pt-4">
          <Typography variant="subtitle1">Discount codes</Typography>
          <TextField fullWidth placeholder="Enter a discount code" variant="outlined" />

          <FormControlLabel
            control={<Checkbox checked={applyAutoDiscounts} onChange={e => setApplyAutoDiscounts(e.target.checked)} />}
            label="Apply all eligible automatic discounts"
          />

          <FormControlLabel
            control={<Checkbox checked={addCustomDiscount} onChange={e => setAddCustomDiscount(e.target.checked)} />}
            label="Add custom order discount"
          />

          {addCustomDiscount && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount type</InputLabel>
                    <Select value={discountType} onChange={e => setDiscountType(e.target.value)}>
                      <MenuItem value="Amount">Amount</MenuItem>
                      <MenuItem value="Percentage">Percentage</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Value"
                    type="number"
                    value={discountValue}
                    onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: discountType === "Amount" ? <InputAdornment position="start">₹</InputAdornment> : null,
                      endAdornment: <InputAdornment position="end">{discountType === "Percentage" ? "%" : "INR"}</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Reason for discount (optional)"
                multiline
                rows={2}
                value={discountReason}
                onChange={e => setDiscountReason(e.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={visibleToCustomer} onChange={e => setVisibleToCustomer(e.target.checked)} />}
                label="Visible to customer"
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="border-t">
          <Button onClick={() => setDiscountModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDiscount}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Shipping Modal */}
      <Dialog open={shippingModal} onClose={() => setShippingModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="border-b">
          Add shipping or delivery
          <IconButton onClick={() => setShippingModal(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="space-y-4 pt-4">
          <Typography variant="subtitle1">Shipping amount</Typography>
          <TextField
            fullWidth
            label="Value"
            type="number"
            value={shippingValue}
            onChange={e => setShippingValue(parseFloat(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              endAdornment: <InputAdornment position="end">INR</InputAdornment>,
            }}
          />
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={2}
            value={shippingReason}
            onChange={e => setShippingReason(e.target.value)}
          />
          <Typography variant="caption" color="textSecondary">
            If set to 0, shipping will be free.
          </Typography>
        </DialogContent>
        <DialogActions className="border-t">
          <Button onClick={() => setShippingModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveShipping}>Done</Button>
        </DialogActions>
      </Dialog>
    </MasterLayout>
  );
}